import React, { useImperativeHandle, useRef } from 'react';
import { Message } from '@meitu/saas-ui';
import {
    getWebmInfoAsync,
    IMAGE_TYPE,
    isWebm as isWebmFn,
    loadImageAsync,
    svg2Png,
    uploadVectorFile,
} from '@meitu/saas-editor-core/es/utils/file-kit';
import { uploadFileToObs } from '@meitu/saas-editor-core/es/utils/upload-kit';
import { parseVector } from '@meitu/saas-editor-core/es/utils/vector-kit';
import { MAX_STAGE_HEIGHT, MAX_STAGE_WIDTH } from '@meitu/saas-editor-core/es/version-types';
import { useLocaleStore } from '@meitu/saas-editor-ui/es/i18n';
import { getFileType, imageUploader, UploadStatusCode, type FileType } from '@meitu/xdesign-widgets/dist/uploadManager';
import useReplaceLayer from '@/editor/panel/hooks/use-replace-layer';
import { editor } from '@/instance';

// 最大文件限制I
const MAX_FILE_SIZE = 20;
const UploadAndReplace = React.forwardRef(
    (
        {
            setUploadLoading = undefined,
            layerId,
            onUpdateReplaceImageNum,
        }: { setUploadLoading?: Function; layerId: string; onUpdateReplaceImageNum: Function },
        ref,
    ) => {
        const { $t } = useLocaleStore();
        const { replaceLayer } = useReplaceLayer();

        // 支持上传的图片格式
        const WIDGETS_IMAGE_TYPE = ['image/png', 'image/jpg', 'image/jpeg', 'image/heic', 'image/webp', 'image/gif'];

        const fileRef = useRef<any>(null);

        const customRequest = async (fileData: any) => {
            try {
                const fileType = fileData.file ? await getFileType(fileData.file) : '';

                let svgInfo;
                // 通用图片走widgets图片上传。有降级云处理策略
                if (WIDGETS_IMAGE_TYPE.includes(fileType)) {
                    const { url } = await imageUploader.checkAndUploadFileToObs(fileData.file, {
                        autoConvertGif: false,
                        limitConfig: {
                            autoResizeWH: MAX_STAGE_WIDTH,
                            maxSize: MAX_FILE_SIZE * 1024 * 1024,
                            accepts: WIDGETS_IMAGE_TYPE as FileType[],
                        },
                    });
                    onSuccess(url);
                } else {
                    if (fileType === 'image/svg+xml') {
                        try {
                            const { pngFile, svgEl } = await svg2Png(fileData.file, true);
                            const { text, info } = await parseVector(svgEl);
                            const imageUrl = await uploadVectorFile(text);
                            svgInfo = {
                                image: imageUrl.url,
                                info,
                                file: fileData.file.url,
                            };
                            fileData.file = pngFile;
                        } catch (error) {
                            console.error('转换失败', error);
                            throw error;
                        }
                    }

                    const fileExtension = fileData.file.name.split('.').pop()?.toLowerCase() || 'jpg';
                    const data = await uploadFileToObs(fileData.file, undefined, fileExtension);
                    const _url = data.url;

                    const isWebm = isWebmFn(_url);

                    const info: any = await (isWebm ? getWebmInfoAsync(_url) : loadImageAsync(_url, false));

                    const width = isWebm ? info.videoWidth : info.width; //图片的宽
                    const height = isWebm ? info.videoHeight : info.height; //图片的高

                    // 判断尺寸
                    const sizeLimit = width <= MAX_STAGE_WIDTH && height <= MAX_STAGE_HEIGHT;

                    if (!sizeLimit) {
                        Message.error(
                            $t((v, { r }) => r(v.imageUpload.sizeLimit, [MAX_STAGE_WIDTH, MAX_STAGE_HEIGHT])),
                        );
                        onError('dimensions');
                        return;
                    }

                    onSuccess(info.src);
                }
            } catch (error: any) {
                let errMsg = 'network';
                if (error.code === UploadStatusCode.TypeError) {
                    Message.error($t((v) => v.imageUpload.supportImageType));
                    errMsg = 'type';
                }
                if (error.code === UploadStatusCode.FileTooLarge) {
                    Message.error($t((v) => v.imageUpload.supportImageType));
                    errMsg = 'size';
                }
                if (error.code === UploadStatusCode.ImageTooLarge) {
                    Message.error($t((v) => v.imageUpload.supportImageType));
                    errMsg = 'dimensions';
                }
                if (error.code === UploadStatusCode.ConvertError) {
                    Message.error($t((v) => v.imageUpload.heicErrorText));
                    errMsg = 'convert';
                }
                onError(errMsg);
            }
        };
        // 上传图片前
        const beforeUpload = async (file: any) => {
            const fileType = await getFileType(file);

            // 判断类型
            const isJpgOrPngOrHeic = IMAGE_TYPE.includes(fileType);
            // 判断大小
            const isLt20M = file.size / 1024 / 1024 < MAX_FILE_SIZE;

            if (!isLt20M) {
                Message.error($t((v, { r }) => r(v.imageUpload.supportImageSize, [MAX_FILE_SIZE])));
                onError('size');
            }

            if (!isJpgOrPngOrHeic) {
                Message.error($t((v) => v.imageUpload.supportImageType));
                onError('type');
            }

            if (isLt20M && isJpgOrPngOrHeic) {
                try {
                    setUploadLoading?.(true);
                } catch (error) {
                    return false;
                }
            }
            return isJpgOrPngOrHeic && isLt20M;
        };

        const onSuccess = async (url: string) => {
            if (!layerId) return;
            try {
                const activeLayer = editor.services.flatLayer.getLayer(layerId);
                const img = await loadImageAsync(url);
                const { width, height } = img;
                const image = { id: '', url, width, height, thumbnail: url };
                replaceLayer(editor, activeLayer, image, () => {
                    editor.services.mask.hide();
                    onUpdateReplaceImageNum();
                });
            } catch (error) {
                console.log(error);
            }
        };

        const onError = (error: any) => {
            setUploadLoading?.(false);
            Message.error(error);
        };

        const onChange = async (e: any) => {
            if (!e.target.files?.[0]) return;
            const file = e.target.files[0];
            const fileData = { file };
            const status = await beforeUpload(file);
            if (status) {
                customRequest(fileData);
            }
        };

        useImperativeHandle(
            ref,
            () => ({
                open: () => {
                    fileRef.current?.click();
                },
            }),
            [],
        );

        return (
            <>
                <input
                    style={{ display: 'none' }}
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg, image/png, image/gif"
                    onChange={onChange}
                />
            </>
        );
    },
);

export default React.memo(UploadAndReplace);
